import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Tabs, Tab, Stack, Chip, Button, Divider, Grid,
    CircularProgress, Alert, List, ListItem, ListItemText, ListItemButton,
    IconButton, Tooltip, useTheme, useMediaQuery,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import PaidIcon from '@mui/icons-material/Paid';
import BalanceIcon from '@mui/icons-material/Balance';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaldoSessoesPaciente from './SaldoSessoesPaciente';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../services/api';
import { registrarRotuloDeRota } from '../services/navegacao';
import { isVisualizador } from '../services/auth';

/**
 * Ficha central do paciente (achado L1).
 *
 * Antes cada assunto do paciente ficava em uma tela separada no menu (pacientes,
 * prontuários, anamnese, sessões, documentos, financeiro) e era preciso procurar o
 * mesmo paciente em cada lista. Aqui as seções ficam em abas, com nome do paciente e
 * próxima ação sempre visíveis.
 *
 * Reaproveita os endpoints já existentes; nenhuma seção duplica regra de negócio.
 * As permissões continuam sendo aplicadas pela API: 403 numa seção vira aviso nessa
 * seção, sem quebrar o resto da ficha.
 */

const ABAS = [
    { chave: 'resumo', rotulo: 'Resumo', icone: <PersonIcon fontSize="small" /> },
    { chave: 'atendimentos', rotulo: 'Atendimentos', icone: <EventIcon fontSize="small" /> },
    { chave: 'clinico', rotulo: 'Prontuário e anamnese', icone: <DescriptionIcon fontSize="small" /> },
    { chave: 'documentos', rotulo: 'Documentos', icone: <FolderIcon fontSize="small" /> },
    { chave: 'financeiro', rotulo: 'Financeiro', icone: <PaidIcon fontSize="small" /> },
    { chave: 'saldo', rotulo: 'Sessões pagas × realizadas', icone: <BalanceIcon fontSize="small" /> },
];

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const data = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');

const STATUS_ATIVOS = ['AGENDADA'];

export default function FichaPaciente() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const soLeitura = isVisualizador();

    const abaUrl = searchParams.get('aba');
    const abaInicial = ABAS.findIndex((a) => a.chave === abaUrl);
    const [aba, setAba] = useState(abaInicial >= 0 ? abaInicial : 0);

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [paciente, setPaciente] = useState(null);
    const [sessoes, setSessoes] = useState([]);
    const [assinatura, setAssinatura] = useState(null);
    const [saldo, setSaldo] = useState(null);
    const [prontuario, setProntuario] = useState(null);
    const [anamneses, setAnamneses] = useState([]);
    const [anexos, setAnexos] = useState([]);
    const [resumoFinanceiro, setResumoFinanceiro] = useState(null);
    const [titulos, setTitulos] = useState([]);
    // Cada seção guarda o próprio aviso: 403 no clínico não deve apagar o resto.
    const [avisos, setAvisos] = useState({});

    const avisar = (secao, texto) => setAvisos((a) => ({ ...a, [secao]: texto }));

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro('');
        setAvisos({});
        try {
            const { data: pac } = await api.get(`/pacientes/${id}`);
            setPaciente(pac);
            if (pac?.nomeCompleto) registrarRotuloDeRota(`paciente:${id}`, pac.nomeCompleto);
        } catch (e) {
            setErro(e?.response?.status === 403
                ? 'Você não tem acesso a este paciente.'
                : 'Não foi possível carregar o paciente.');
            setCarregando(false);
            return;
        }

        // Cada seção falha de forma independente.
        const carregas = [
            api.get(`/pacientes/${id}/saldo-sessoes`)
                .then((r) => setSaldo(r.data))
                .catch((e) => avisar('saldo', mensagem(e, 'saldo de sessões'))),

            api.get(`/sessoes/paciente/${id}`)
                .then((r) => setSessoes(Array.isArray(r.data) ? r.data : []))
                .catch((e) => avisar('atendimentos', mensagem(e, 'atendimentos'))),

            api.get(`/assinaturas-paciente/paciente/${id}/ativa`)
                .then((r) => setAssinatura(r.status === 204 ? null : r.data))
                .catch((e) => { if (e?.response?.status !== 404) avisar('assinatura', mensagem(e, 'plano')); }),

            api.get(`/prontuarios/paciente/${id}`)
                .then((r) => setProntuario(r.status === 204 ? null : r.data))
                .catch((e) => { if (e?.response?.status !== 404) avisar('clinico', mensagem(e, 'prontuário')); }),

            api.get(`/anamneses/paciente/${id}`)
                .then((r) => setAnamneses(Array.isArray(r.data) ? r.data : (r.data ? [r.data] : [])))
                .catch((e) => { if (e?.response?.status !== 404) avisar('anamnese', mensagem(e, 'anamnese')); }),

            api.get(`/anexos/paciente/${id}`)
                .then((r) => setAnexos(Array.isArray(r.data) ? r.data : []))
                .catch((e) => avisar('documentos', mensagem(e, 'documentos'))),

            api.get('/contas-receber/resumo', { params: { pacienteId: id } })
                .then((r) => setResumoFinanceiro(r.data))
                .catch((e) => avisar('financeiro', mensagem(e, 'financeiro'))),

            api.get('/contas-receber/paginado', { params: { pacienteId: id, page: 0, size: 10 } })
                .then((r) => setTitulos(r.data?.content ?? []))
                .catch(() => { /* o aviso do resumo já cobre a seção */ }),
        ];

        await Promise.allSettled(carregas);
        setCarregando(false);
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);

    const trocarAba = (_e, nova) => {
        setAba(nova);
        // Aba na URL: permite voltar para a mesma seção e compartilhar o link.
        const params = new URLSearchParams(searchParams);
        params.set('aba', ABAS[nova].chave);
        setSearchParams(params, { replace: true });
    };

    const proximaSessao = sessoes
        .filter((s) => STATUS_ATIVOS.includes(s.statusSessao) && s.dataHora && new Date(s.dataHora) >= new Date())
        .sort((a, b) => new Date(a.dataHora) - new Date(b.dataHora))[0];

    const evolucoesPendentes = sessoes.filter(
        (s) => s.dataHora && new Date(s.dataHora) < new Date() && STATUS_ATIVOS.includes(s.statusSessao)
    );

    if (carregando) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    if (erro) {
        return (
            <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={carregar}>Tentar de novo</Button>}
            >
                {erro}
            </Alert>
        );
    }

    return (
        <Box>
            {/* Cabeçalho fixo da ficha: nome e próxima ação sempre visíveis */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={1.5}
                    sx={{ justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center' }}
                >
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                            <IconButton onClick={() => navigate('/pacientes')} aria-label="Voltar para a lista de pacientes" size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" sx={{ fontWeight: 700 }}>
                                {paciente?.nomeCompleto || `Paciente #${id}`}
                            </Typography>
                            {assinatura && (
                                <Chip
                                    size="small"
                                    label={`Plano: ${assinatura.nomePlanoAtendimento || 'ativo'} — ${assinatura.sessoesRestantes ?? 0} sessão(ões) restantes`}
                                    sx={{ bgcolor: '#e8f5e9', color: '#1b5e20', fontWeight: 600 }}
                                />
                            )}
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 5 }}>
                            {proximaSessao
                                ? `Próximo atendimento: ${dataHora(proximaSessao.dataHora)}`
                                : 'Sem atendimento futuro agendado'}
                            {evolucoesPendentes.length > 0
                                ? ` · ${evolucoesPendentes.length} evolução(ões) a concluir`
                                : ''}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Recarregar a ficha">
                            <IconButton onClick={carregar} aria-label="Recarregar a ficha do paciente">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        {!soLeitura && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => navigate(`/pacientes/${id}`)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Editar cadastro
                                </Button>
                                {/* Ação principal única da tela */}
                                <Button
                                    variant="contained"
                                    startIcon={<EventIcon />}
                                    onClick={() => navigate(`/sessoes/novo?idPaciente=${id}`)}
                                    sx={{ textTransform: 'none', bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                                >
                                    Agendar atendimento
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Tabs
                    value={aba}
                    onChange={trocarAba}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label="Seções da ficha do paciente"
                    sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
                >
                    {ABAS.map((a, i) => (
                        <Tab
                            key={a.chave}
                            icon={a.icone}
                            iconPosition="start"
                            label={a.rotulo}
                            id={`ficha-tab-${i}`}
                            aria-controls={`ficha-painel-${i}`}
                        />
                    ))}
                </Tabs>

                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* ------------------------------------------------ Resumo */}
                    <Painel indice={0} atual={aba}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Secao titulo="Contato">
                                    <Campo rotulo="Telefone" valor={paciente?.telefone} />
                                    <Campo rotulo="E-mail" valor={paciente?.email} />
                                    <Campo rotulo="Nascimento" valor={data(paciente?.dataNascimento)} />
                                    <Campo rotulo="CPF" valor={paciente?.cpf} />
                                </Secao>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Secao titulo="Emergência">
                                    <Campo rotulo="Contato" valor={paciente?.nomeContatoEmergencia} />
                                    <Campo rotulo="Telefone" valor={paciente?.telefoneContatoEmergencia} />
                                </Secao>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Secao titulo="Atendimentos">
                                    <Campo rotulo="Total registrado" valor={String(sessoes.length)} />
                                    <Campo rotulo="Próximo" valor={proximaSessao ? dataHora(proximaSessao.dataHora) : 'nenhum agendado'} />
                                    <Campo rotulo="Evoluções a concluir" valor={String(evolucoesPendentes.length)} />
                                </Secao>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Secao titulo="Financeiro">
                                    {avisos.financeiro
                                        ? <Alert severity="warning" sx={{ mt: 1 }}>{avisos.financeiro}</Alert>
                                        : (
                                            <>
                                                <Campo rotulo="Em aberto" valor={brl(resumoFinanceiro?.totalEmAberto)} />
                                                <Campo rotulo="Vencido" valor={brl(resumoFinanceiro?.totalVencido)} />
                                                <Campo rotulo="Recebido" valor={brl(resumoFinanceiro?.totalRecebido)} />
                                            </>
                                        )}
                                </Secao>
                            </Grid>
                        </Grid>
                    </Painel>

                    {/* ------------------------------------------- Atendimentos */}
                    <Painel indice={1} atual={aba}>
                        {avisos.atendimentos && <Alert severity="warning" sx={{ mb: 2 }}>{avisos.atendimentos}</Alert>}
                        {sessoes.length === 0 && !avisos.atendimentos && <Vazio texto="Nenhum atendimento registrado para este paciente." />}
                        <List disablePadding>
                            {[...sessoes]
                                .sort((a, b) => new Date(b.dataHora) - new Date(a.dataHora))
                                .map((s) => (
                                    <ListItemButton
                                        key={s.id}
                                        onClick={() => navigate(`/sessoes/${s.id}/evolucao`)}
                                        sx={{ borderBottom: '1px solid #f0f0f0' }}
                                    >
                                        <ListItemText
                                            primary={dataHora(s.dataHora)}
                                            secondary={`${s.statusSessao || '—'} · ${s.modalidadeSessao || '—'}${s.temEvolucao ? ' · evolução registrada' : ' · sem evolução'}`}
                                        />
                                        <Chip
                                            size="small"
                                            label={s.statusSessao || '—'}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </ListItemButton>
                                ))}
                        </List>
                    </Painel>

                    {/* ----------------------------- Prontuário e anamnese */}
                    <Painel indice={2} atual={aba}>
                        {avisos.clinico && <Alert severity="warning" sx={{ mb: 2 }}>{avisos.clinico}</Alert>}
                        <Secao titulo="Prontuário">
                            {prontuario ? (
                                <Stack spacing={0.5}>
                                    <Campo rotulo="Número" valor={prontuario.numeroRegistro} />
                                    <Campo rotulo="Abertura" valor={data(prontuario.dataAbertura)} />
                                    <Campo rotulo="Situação" valor={prontuario.status} />
                                    <Box>
                                        <Button size="small" onClick={() => navigate(`/prontuarios/${prontuario.id_prontuario || prontuario.id}`)}>
                                            Abrir prontuário
                                        </Button>
                                    </Box>
                                </Stack>
                            ) : !avisos.clinico && <Vazio texto="Prontuário ainda não aberto. Ele é criado ao registrar a primeira evolução." />}
                        </Secao>

                        <Divider sx={{ my: 2 }} />

                        <Secao titulo="Anamnese">
                            {avisos.anamnese && <Alert severity="warning" sx={{ mb: 1 }}>{avisos.anamnese}</Alert>}
                            {anamneses.length === 0 && !avisos.anamnese && <Vazio texto="Nenhuma anamnese registrada." />}
                            <List disablePadding>
                                {anamneses.map((a) => (
                                    <ListItemButton
                                        key={a.id || a.id_anamnese}
                                        onClick={() => navigate(`/anamnese/${a.id || a.id_anamnese}`)}
                                    >
                                        <ListItemText primary={data(a.dataRegistro || a.data)} secondary="Abrir anamnese" />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Secao>
                    </Painel>

                    {/* --------------------------------------------- Documentos */}
                    <Painel indice={3} atual={aba}>
                        {avisos.documentos && <Alert severity="warning" sx={{ mb: 2 }}>{avisos.documentos}</Alert>}
                        {anexos.length === 0 && !avisos.documentos && <Vazio texto="Nenhum anexo ou documento deste paciente." />}
                        <List disablePadding>
                            {anexos.map((an) => (
                                <ListItem key={an.id || an.id_anexo} divider>
                                    <ListItemText
                                        primary={an.nomeArquivo || an.descricao || 'Arquivo'}
                                        secondary={data(an.dataUpload || an.dataCriacao)}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Box sx={{ mt: 2 }}>
                            <Button size="small" onClick={() => navigate(`/pacientes/${id}/anexos`)}>
                                Gerenciar anexos
                            </Button>
                        </Box>
                    </Painel>

                    {/* --------------------------------------------- Financeiro */}
                    <Painel indice={4} atual={aba}>
                        {avisos.financeiro && <Alert severity="warning" sx={{ mb: 2 }}>{avisos.financeiro}</Alert>}
                        {!avisos.financeiro && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Indicador rotulo="Em aberto" valor={brl(resumoFinanceiro?.totalEmAberto)} cor="#0277bd" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Indicador rotulo="Vencido" valor={brl(resumoFinanceiro?.totalVencido)} cor="#ef6c00" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Indicador rotulo="Recebido" valor={brl(resumoFinanceiro?.totalRecebido)} cor="#2e7d32" />
                                </Grid>
                            </Grid>
                        )}

                        {titulos.length === 0 && !avisos.financeiro && <Vazio texto="Nenhum título financeiro deste paciente." />}
                        <List disablePadding>
                            {titulos.map((t) => (
                                <ListItemButton key={t.id} onClick={() => navigate(`/contas-receber/${t.id}`)} divider>
                                    <ListItemText
                                        primary={`${t.descricao} — ${brl(t.valor)}`}
                                        secondary={`Vence ${data(t.dataVencimento)} · ${t.statusFinanceiro} · recebido ${brl(t.valorPago)} · em aberto ${brl(t.saldoEmAberto)}`}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                        <Box sx={{ mt: 2 }}>
                            <Button size="small" onClick={() => navigate('/contas-receber')}>
                                Ver todos os títulos
                            </Button>
                        </Box>
                    </Painel>

                    {/* ------------------------- Sessões pagas × realizadas */}
                    <Painel indice={5} atual={aba}>
                        <SaldoSessoesPaciente
                            saldo={saldo}
                            loading={carregando}
                            erro={avisos.saldo}
                        />
                    </Painel>
                </Box>
            </Paper>
        </Box>
    );
}

function mensagem(e, secao) {
    const status = e?.response?.status;
    if (status === 403) return `Você não tem permissão para ver ${secao} deste paciente.`;
    if (!status) return `Sem resposta do servidor ao carregar ${secao}.`;
    return `Não foi possível carregar ${secao}.`;
}

function Painel({ indice, atual, children }) {
    if (indice !== atual) return null;
    return (
        <div role="tabpanel" id={`ficha-painel-${indice}`} aria-labelledby={`ficha-tab-${indice}`}>
            {children}
        </div>
    );
}

function Secao({ titulo, children }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {titulo}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {children}
        </Box>
    );
}

function Campo({ rotulo, valor }) {
    return (
        <Stack direction="row" sx={{ justifyContent: 'space-between', py: 0.4, gap: 2 }}>
            <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{valor || '—'}</Typography>
        </Stack>
    );
}

function Indicador({ rotulo, valor, cor }) {
    return (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: cor }}>{valor}</Typography>
        </Paper>
    );
}

function Vazio({ texto }) {
    return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {texto}
        </Typography>
    );
}
